import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, TextInput, Typography } from '@guildeducationinc/recess';
import { ProgramContext } from '@src/contexts/ProgramContextProvider';
import { useCurrentClientConfig } from '@src/hooks/useCurrentClientConfig';
import { useCurrentCurrency } from '@src/hooks/useCurrentCurrency';
import { RuleType, } from '@src/types';
import { getTodayDateString, isFutureDateInTimezone } from '@src/utilities/dateHelper';
import { Currency, DatePicker, MuiSingleSelect, useCurrencyConversion, useLocale, } from 'pe-core-frontend';
import { useContext, useEffect, useMemo, useState } from 'react';
export const RequestFunding = ({ paymentRequest, onChange, fundingCap, fundingLimit, currency, clientId, errors, }) => {
    const { formatCurrency } = useLocale();
    const { getPolicy, program } = useContext(ProgramContext);
    const expensePolicy = getPolicy(RuleType.EXPENSE, program);
    const currencyDefault = useCurrentCurrency({ clientId });
    const { clientConfig } = useCurrentClientConfig();
    const fundingItems = useMemo(() => {
        const items = [
            { label: 'Course costs', visible: expensePolicy?.allowCourses !== false },
            { label: 'Required fees', visible: expensePolicy?.allowFees !== false },
            { label: 'Required materials', visible: expensePolicy?.allowMaterials !== false },
            { label: 'Required books', visible: expensePolicy?.allowBooks !== false },
        ];
        return items.filter(item => item.visible);
    }, [
        expensePolicy?.allowCourses,
        expensePolicy?.allowFees,
        expensePolicy?.allowMaterials,
        expensePolicy?.allowBooks,
    ]);
    // Currency conversion state — restore from extraData when navigating back
    const [selectedCurrency, setSelectedCurrency] = useState(paymentRequest.extraData?.originalAmountRequestedCurrency || currencyDefault);
    const [currencyInput, setCurrencyInput] = useState(paymentRequest.extraData?.originalAmountRequestedCurrency || currencyDefault);
    const [receiptDate, setReceiptDate] = useState(paymentRequest.extraData?.receiptDate);
    // Track the user's typed amount (in the selected foreign currency)
    const [localAmount, setLocalAmount] = useState(paymentRequest.extraData?.originalAmountRequested
        ? String(paymentRequest.extraData.originalAmountRequested)
        : paymentRequest.amountRequested
            ? String(paymentRequest.amountRequested)
            : '');
    // Sync receiptDate when extraData changes externally
    useEffect(() => {
        setReceiptDate(paymentRequest.extraData?.receiptDate);
    }, [paymentRequest.extraData?.receiptDate]);
    const amountToConvert = Number(localAmount) || 0;
    const isValidDateString = (dateStr) => {
        if (!dateStr || dateStr === 'Invalid Date')
            return false;
        const date = new Date(dateStr);
        return !isNaN(date.getTime());
    };
    const isForeignCurrency = selectedCurrency !== currencyDefault && !!selectedCurrency;
    const isFutureDate = useMemo(() => {
        if (!receiptDate)
            return false;
        const clientTimezone = clientConfig?.timezone || 'UTC';
        return isFutureDateInTimezone(receiptDate, clientTimezone);
    }, [receiptDate, clientConfig?.timezone]);
    const effectiveReceiptDate = useMemo(() => {
        if (!isFutureDate)
            return receiptDate;
        return getTodayDateString();
    }, [isFutureDate, receiptDate]);
    const isValidConversionSpec = !!receiptDate &&
        isValidDateString(receiptDate) &&
        !!selectedCurrency &&
        !!currencyDefault &&
        amountToConvert > 0 &&
        isForeignCurrency;
    const conversionSpec = useMemo(() => ({
        receiptDate: effectiveReceiptDate || '',
        baseCurrency: selectedCurrency || currencyDefault,
        quoteCurrency: currencyDefault,
        amountToConvert,
    }), [effectiveReceiptDate, selectedCurrency, currencyDefault, amountToConvert]);
    const shouldFetchConversion = !!receiptDate &&
        !!selectedCurrency &&
        !!currencyDefault &&
        amountToConvert > 0 &&
        isForeignCurrency;
    const { data: conversionData, loading: conversionLoading, error: conversionError, } = useCurrencyConversion(conversionSpec, !shouldFetchConversion);
    const currencyOptions = Object.entries(Currency)
        .map(([code, name]) => ({
        value: code,
        label: `${code} - ${name}`,
    }))
        .sort((a, b) => a.value.localeCompare(b.value));
    const filteredCurrencyOptions = useMemo(() => {
        return currencyOptions.filter(option => !currencyInput ||
            option.label.toLowerCase().indexOf(currencyInput?.toLowerCase() || '') !== -1);
    }, [currencyOptions, currencyInput]);
    // When conversion data arrives, update the parent with converted amount + extra_data
    useEffect(() => {
        if (!isForeignCurrency || !conversionData || conversionLoading)
            return;
        const intermediateAmount = Number(Number(localAmount || '0') * (conversionData?.exchangeRates?.[0] || 0));
        const baseConversion = {
            baseAmount: Number(localAmount || '0'),
            baseCurrency: selectedCurrency,
            quoteAmount: intermediateAmount,
            quoteCurrency: 'USD',
            exchangeRateDate: conversionData?.exchangeRateDate,
            exchangeRate: conversionData?.exchangeRates?.[0] || 0,
        };
        const conversionCalculations = currencyDefault === 'USD'
            ? [baseConversion]
            : [
                baseConversion,
                {
                    baseAmount: intermediateAmount,
                    baseCurrency: 'USD',
                    quoteAmount: Number(Number(intermediateAmount || '0') * (conversionData?.exchangeRates?.[1] || 0)),
                    quoteCurrency: currencyDefault || '',
                    exchangeRateDate: conversionData?.exchangeRateDate,
                    exchangeRate: conversionData?.exchangeRates?.[1] || 0,
                },
            ];
        onChange({
            ...paymentRequest,
            amountRequested: Number(conversionData.convertedAmount || localAmount || '0'),
            extraData: {
                ...paymentRequest.extraData,
                originalAmountRequested: Number(localAmount || '0'),
                originalAmountRequestedCurrency: selectedCurrency,
                receiptDate,
                exchangeRate: conversionData.totalExchangeRate,
                exchangeRateDate: conversionData.exchangeRateDate,
                currencyCalculations: conversionCalculations,
            },
        });
    }, [conversionData, conversionLoading]);
    const [amountError, setAmountError] = useState(undefined);
    const handleAmountChange = (e) => {
        const value = e.target.value;
        setLocalAmount(value);
        if (value && Number(value) <= 0) {
            setAmountError('must be greater than 0');
        }
        else {
            setAmountError(undefined);
        }
        if (!isForeignCurrency) {
            // Same currency — update amountRequested directly, clear conversion fields
            onChange({
                ...paymentRequest,
                amountRequested: value ? value : 0,
                extraData: {
                    ...paymentRequest.extraData,
                    originalAmountRequested: undefined,
                    originalAmountRequestedCurrency: undefined,
                    receiptDate: undefined,
                    exchangeRate: undefined,
                    exchangeRateDate: undefined,
                    currencyCalculations: undefined,
                },
            });
        }
        // When foreign currency is selected, the useEffect above handles updating
        // after conversion data is fetched
    };
    const handleCurrencyChange = (currencyCode) => {
        setSelectedCurrency(currencyCode);
        if (currencyCode === currencyDefault) {
            // Switched back to base currency — update amountRequested directly, clear conversion fields
            onChange({
                ...paymentRequest,
                amountRequested: localAmount ? localAmount : 0,
                extraData: {
                    ...paymentRequest.extraData,
                    originalAmountRequested: undefined,
                    originalAmountRequestedCurrency: undefined,
                    receiptDate: undefined,
                    exchangeRate: undefined,
                    exchangeRateDate: undefined,
                    currencyCalculations: undefined,
                },
            });
        }
        else {
            // Switched to foreign currency — clear amountRequested until conversion completes
            onChange({
                ...paymentRequest,
                amountRequested: undefined,
                extraData: {
                    ...paymentRequest.extraData,
                    originalAmountRequestedCurrency: currencyCode,
                },
            });
        }
    };
    return (_jsxs("div", { className: 'flex flex-col gap-4 mt-4', children: [_jsx(Typography, { variant: 'title3', children: "Request funding" }), _jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(Typography, { variant: 'body1', children: "Estimate total costs for this term or billing period." }), _jsx(Typography, { variant: 'body1', weight: '700', children: "This is the total amount you can be paid for expenses related to this term." }), _jsx(Typography, { variant: 'body1', children: "Approved funds can be used to cover:" }), _jsx("ul", { className: 'list-disc pl-6', children: fundingItems.map(item => (_jsx("li", { children: _jsx(Typography, { variant: 'body1', children: item.label }) }, item.label))) }), _jsx(Typography, { variant: 'label2', children: "Each individual expense will still require review and approval before payment is issued." })] }), _jsxs("div", { className: 'bg-[#ebe8de] border-l-4 border-[#e7651c] p-3 flex flex-col gap-3', children: [_jsxs("div", { className: 'flex flex-row gap-5', children: [_jsxs("div", { className: 'flex-1', children: [_jsx(TextInput, { label: 'Total costs', type: 'number', value: localAmount, onChange: handleAmountChange, errors: amountError || errors?.amountRequested, required: true }), _jsx("span", { children: `You can request up to: ${formatCurrency(fundingCap, currency, true)}${fundingLimit !== fundingCap ? ` (${formatCurrency(fundingLimit, currency, true)} remaining)` : ''}. Final amount is subject to approval.` })] }), _jsx("div", { className: 'flex-1', children: _jsx("div", { className: 'w-44', children: _jsx(MuiSingleSelect, { label: 'Currency', value: selectedCurrency || '', onChange: e => {
                                            handleCurrencyChange(e.target.value);
                                            setCurrencyInput(e.target.value);
                                        }, options: filteredCurrencyOptions, showSearchIcon: true, onSearch: setCurrencyInput, inputValue: currencyInput, required: true, getOptionSelectedLabel: option => String(option.value) }) }) })] }), isForeignCurrency && (_jsxs(_Fragment, { children: [_jsx("div", { className: 'mt-2 mb-1', children: _jsx(DatePicker, { label: 'Purchase date (mm/dd/yyyy) *', value: receiptDate || '', onChange: setReceiptDate, errors: errors?.receiptDate }) }), isValidConversionSpec && (_jsxs("div", { className: 'bg-[#f6f6f6] rounded-lg p-4 mt-1 mb-2 border border-[#e0e0e0] flex flex-col gap-1', children: [_jsxs("div", { className: 'font-semibold text-base', children: ["Amount converted to ", currencyDefault, " *"] }), conversionLoading && _jsx("div", { children: "Loading..." }), conversionError && _jsx("div", { style: { color: 'red' }, children: "Conversion error" }), conversionData && (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'text-sm text-[#666]', children: ["Exchange rate on date of purchase was:", ' ', Number(conversionData.totalExchangeRate).toFixed(2)] }), _jsx("div", { className: 'text-lg font-bold text-[#222]', children: formatCurrency(Number(conversionData.convertedAmount), currencyDefault, true) }), _jsxs(Typography, { variant: 'label2', children: ["(", formatCurrency(amountToConvert, selectedCurrency || '', true), ")"] }), isFutureDate && (_jsx(Alert, { type: 'warning', display: true, hideClose: true, className: 'mt-2', children: "This amount was converted using the most recent exchange rate. Final reimbursement amounts may differ if exchange rates change before funds are disbursed." }))] }))] }))] }))] })] }));
};
